import { ErrorMessage, Field, Form, Formik, FormikHelpers } from 'formik';
import Image from 'next/image';
import React, { Dispatch, SetStateAction, useRef } from 'react';
import { styled } from 'styled-components';
import { RatingField } from '../../../shared-components/RatingGroupStars/RatingGroupStars';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import axios from 'axios';
import { Review } from '../../../pages/[slug]/[slug2]';
import client from '../../../apollo-client';
import { BOOKING_DATA } from '../../../api/fetch_user_details';

interface IProps {
  setIsModalOpen: (open: boolean) => void;
  onClose: () => void;
  id: number | string | undefined;
  selectedReviewData: Review | null;
  setSelectedReviewData: React.Dispatch<React.SetStateAction<Review | null>>;
  fetchReviews: () => Promise<void>;
  onSubmit?: (reviewData: any) => Promise<void>;
  initialData?: {
    name: string;
    email: string;
    rating: number;
    reviewTitle: string;
    reviewText: string;
    tourDate: string;
    productId: string;
    cityId: string;
  };
}

interface Props {
  isChecked: boolean;
}
const CheckBoxTextStyle = styled.p<Props>`
  opacity: ${(props: any) => (props.isChecked ? '1' : '0.5')};
  width: 90%;
`;

const AddReviewModal = ({
  onClose,
  onSubmit,
  initialData,
  setIsModalOpen,
  id,
  selectedReviewData,
  setSelectedReviewData,
  fetchReviews
}: IProps) => {
  const modalRef: any = useRef(null);

  const closeModal = () => {
    onClose();
    setIsModalOpen(false);
    setSelectedReviewData(null);
  };
  const urlParams = new URLSearchParams(window.location.search);
  const bookingId = urlParams.get('id');

  const validationSchema = Yup.object().shape({
    name: Yup.string().when([], {
      is: () => !bookingId,
      then: schema =>
        schema
          .trim()
          .test(
            'no-blank-spaces',
            'Name cannot contain only blank spaces',
            value => {
              return !/^\s*$/.test(value || '');
            }
          )
          .min(2, 'Name must be at least 2 characters')
          .required('Name is required'),
      otherwise: schema => schema.optional()
    }),
    rating: Yup.number()
      .min(0.5, 'Please select a rating')
      .required('Please select a rating'),
    review: Yup.string()
      .trim()
      .test(
        'no-blank-spaces',
        'Review cannot contain only blank spaces',
        value => {
          return !/^\s*$/.test(value || '');
        }
      )
      .min(8, 'Minimum Length should be 8 characters!')
      .required('Review is required'),
    termsConditions: Yup.boolean()
      .oneOf([true], 'Please agree to the Terms & Conditions')
      .required('Please agree to the Terms & Conditions')
  });

  const handleSubmit = async (values: any) => {
    // Get booking ID from URL

    // Case 1: Pre-filled initial data (usually from booking)
    if (initialData) {
      try {
        if (onSubmit) {
          // Extract first name from the full name
          const fullName = initialData.name;
          const firstName = fullName.split(' ')[0];

          await onSubmit({
            ...values,
            name: firstName, // Use only the first name
            email: initialData.email,
            productId: initialData.productId,
            cityId: initialData.cityId,
            tourDate: initialData.tourDate,
            bookingId // Include booking ID in submission
          });
        }
        closeModal();
        return;
      } catch (error) {
        console.error('Error submitting review 3:', error);
        return;
      }
    }

    // Case 3: Guest submission with valid booking ID
    if (bookingId) {
      return handleGuestReview(values, bookingId);
    }

    // Proceed with regular logged-in submission
    return handleUserReview(values);
  };

  const handleGuestReview = async (values: any, bookingId: string) => {
    try {
      // 1. First fetch booking details to get customer name
      const bookingResponse = await client.query({
        query: BOOKING_DATA,
        variables: { bookingId }
      });

      const booking = bookingResponse.data?.boatnew_booking?.[0];
      const fullName = booking?.customer?.contact_name || 'Guest';
      const firstName = fullName.split(' ')[0];
      const url = `${process.env.NEXT_PUBLIC_FIREBASE_WEBSITE_CONNECTION}/strapi-product-review`;
      const data = {
        productId: id?.toString(),
        rating: values.rating,
        review: values.review,
        bookingId,
        userName: firstName
      };

      const response = await axios.post(url, data);

      fetchReviews();
      setIsModalOpen(false);
      toast.success(response.data.message);
    } catch (error) {
      console.error('Guest review submission error:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('An error occurred while submitting the review');
      }
    }
  };

  const handleUserReview = async (values: any) => {
    try {
      const headers = {
        'Content-Type': 'application/json'
      };

      const url = `${process.env.NEXT_PUBLIC_FIREBASE_WEBSITE_CONNECTION}/product-review`;
      const data = {
        productId: id?.toString(),
        rating: values.rating,
        review: values.review,
        userName: values.name
      };

      const response = await axios.post(url, data, { headers });
      fetchReviews();
      setIsModalOpen(false);
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Error submitting review');
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-[1150] bg-gray-800 bg-opacity-50 flex items-center justify-center">
        <div
          ref={modalRef}
          className="bg-white rounded-md w-[90%] md:w-[530px] px-6 py-4 lg:px-6 lg:py-5 xl:px-12 xl:py-7"
        >
          <div className="flex justify-between mb-4 xl:mb-8 items-center">
            <h2 className="text-sm md:text-lg">
              Share your experience with us
            </h2>
            <span className="cursor-pointer" onClick={closeModal}>
              <Image
                src={'/close-icon.svg'}
                alt="close-icon"
                width={20}
                height={20}
              />
            </span>
          </div>
          <div>
            <Formik
              initialValues={{
                name: selectedReviewData ? selectedReviewData.name : '',
                review: selectedReviewData ? selectedReviewData.review : '',
                rating: selectedReviewData ? selectedReviewData.rating : 0,
                termsConditions: false
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting, setFieldValue, resetForm, values }) => (
                <Form>
                  <div className="flex flex-col gap-4 xl:gap-6">
                    {!bookingId && (
                      <div className="flex flex-col gap-2">
                        <label className="block text-[14px] xl:text-[16px] font-[500] text-[#4F4F4F]">
                          Name
                        </label>
                        <Field
                          id="name"
                          name="name"
                          type="text"
                          placeholder="Enter Name"
                          className="block focus:outline-none w-[100%] rounded-[10px] px-3 py-2 md:px-5 md:py-3 bg-[#EEEEEE] text-base xxsm:placeholder:text-[14px] md:placeholder:text-[16px]"
                        />
                        <ErrorMessage
                          name="name"
                          component="div"
                          className="text-red-400 xxsm:mt-0 md:mt-2 xxsm:text-[12px] md:text-base"
                        />
                      </div>
                    )}

                    <div className="flex flex-col gap-2">
                      <label className="block text-[14px] xl:text-[16px] font-[500] text-[#4F4F4F]">
                        Rating
                      </label>
                      <Field
                        name="rating"
                        component={RatingField}
                        setFieldValue={setFieldValue}
                      />
                      <ErrorMessage
                        name="rating"
                        component="div"
                        className="text-red-400 xxsm:mt-0 md:mt-2 xxsm:text-[12px] md:text-base"
                      />
                    </div>

                    <div>
                      <Field
                        id="review"
                        name="review"
                        as="textarea"
                        placeholder="Write Your Review"
                        className="block focus:outline-none w-[100%] rounded-[10px] px-3 py-2 md:px-5 md:py-4 bg-[#EEEEEE] md:h-[120px] lg:h-[140px] xl:h-[150px] min-h-[100px] text-base xxsm:placeholder:text-[14px] md:placeholder:text-[16px]"
                      />
                      <ErrorMessage
                        name="review"
                        component="div"
                        className="text-red-400 xxsm:mt-0 md:mt-2 xxsm:text-[12px] md:text-base"
                      />
                    </div>
                  </div>

                  <div className="flex items-start gap-3 pt-0 md:pt-3 flex-col">
                    <div className="flex items-start gap-3 pt-3">
                      <Field
                        type="checkbox"
                        name="termsConditions"
                        checked={values.termsConditions}
                        onChange={(e: any) => {
                          setFieldValue('termsConditions', e.target.checked);
                        }}
                        className="mt-1 cursor-pointer"
                      />
                      <CheckBoxTextStyle
                        isChecked={values.termsConditions}
                        className="xxsm:text-sm text-justify md:text-sm font-[500] text-[#4F4F4F]"
                      >
                        I certify that this review is based on my experience and
                        is my genuine opinion, and have not beet offered any
                        incentive or payment origniating from the establishment
                        to rewrite this review. I understand that a See Sight
                        Tours has a zero tolerance policy on fake reviews.
                      </CheckBoxTextStyle>
                    </div>

                    <ErrorMessage
                      name="termsConditions"
                      component="div"
                      className="text-red-400 xxsm:text-[14px] md:text-base"
                    />
                  </div>

                  <div className="flex justify-end gap-3">
                    <button
                      className="py-2 flex justify-center items-center w-[120px] focus:outline-none text-[14px] sm:text-base xxsm:font-normal md:font-semibold bg-[#1F2C5B] text-white float-right rounded-[10px] xxsm:mt-2  xl:mt-5"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Loading...' : 'Submit'}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddReviewModal;
